"use client"

import Script from "next/script"

/**
 * Calendario de GHL embebido (dominio white-label link.usekickoff.com). Lo usan
 * /demo y la tarjeta "Agendar una demo" de Referidos.
 *
 * ⚠️ El host TIENE que estar en frame-src y script-src del CSP de next.config.ts
 * o el navegador bloquea el iframe sin mensaje visible.
 */

/**
 * GHL identifica el iframe por su id para saber a quién redimensionar: el id
 * sale del último segmento del path del widget (sin el query del prellenado).
 */
export function ghlIframeId(calendarUrl: string): string {
  const path = calendarUrl.split(/[?#]/)[0]
  return `${path.split("/").pop() || "booking"}_redy`
}

/** El form_embed.js del mismo host que el calendario, o null si `src` no es una URL http(s) absoluta. */
function embedScriptUrl(src: string): string | null {
  try {
    const url = new URL(src)
    if (url.protocol !== "https:" && url.protocol !== "http:") return null
    return new URL("/js/form_embed.js", url).toString()
  } catch {
    return null
  }
}

export function GhlBookingEmbed({
  src,
  iframeId,
  title,
  fallbackLabel,
}: {
  src: string
  iframeId: string
  title: string
  /** "¿No carga el calendario? Ábrelo en otra pestaña", ya traducido por quien lo usa. */
  fallbackLabel: string
}) {
  // form_embed.js es lo que ajusta la altura del iframe por postMessage. Sin él
  // el calendario queda recortado o con doble scroll. Se sirve del mismo host.
  const embedScript = embedScriptUrl(src)

  // URL mal formada, relativa o de otro protocolo: sin iframe ni script (`new URL` rompería el render)
  // ni enlace a un destino que no es http(s). Queda solo el texto del respaldo.
  if (!embedScript) {
    return <p className="text-center text-[13px] text-redy-secondary pb-3">{fallbackLabel}</p>
  }

  return (
    <>
      <iframe
        src={src}
        id={iframeId}
        title={title}
        scrolling="no"
        className="w-full border-0 overflow-hidden min-h-[720px] rounded-[20px]"
      />
      <Script src={embedScript} strategy="afterInteractive" />
      {/* Respaldo: si el widget no pinta (bloqueador de rastreadores, dominio
          del padre no reconocido por GHL, script caído), la página no puede
          quedar en una caja blanca sin salida. El enlace directo siempre
          funciona. Abre en pestaña nueva por ser destino externo. */}
      <p className="text-center text-[13px] text-redy-secondary pb-3">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:text-redy-charcoal transition-colors"
        >
          {fallbackLabel}
        </a>
      </p>
    </>
  )
}
